import {
  GREY_100,
  GREY_300,
  GREY_400,
  GREY_500,
  GREY_800,
  GREY_900,
  Icons,
  PURPLE_600,
  SIZING_2_5,
  SPACING_1,
  SPACING_2,
  SPACING_4,
  remCalc,
} from '@mnd-frontend/ui';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useEnvironmentVariables } from '../../../../contexts/EnvironmentVariablesContext';
import { useNewsroomSetup } from '../../../../contexts/NewsroomSetupContext';
import { getCloudinaryImageUrl } from '../../../../lib/getCloudinaryImageUrl';
import { EmptyPressRelease, HeaderWithColoredUnderline, WrapperForEmptyReleases } from './shared';

const BrandingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 6px;
  height: 100%;
`;

const NewsroomHeader = styled.div`
  width: 100%;
  max-width: ${remCalc(576)};
  height: ${remCalc(60)};
  background: white;
  border: 2px solid ${GREY_400};
  border-width: 0 0 2px 0;
  padding: 0 2rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${SPACING_2};
`;

const Figure = styled.figure`
  flex: 1.5;
  max-width: ${remCalc(160)};
  margin: 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  img {
    max-height: 2.5rem;
    max-width: 5rem;
  }
`;

const LogoPlaceholder = styled.div`
  color: ${GREY_800};
  background-color: ${GREY_300};
  font-size: ${remCalc(10)};
  white-space: nowrap;
  padding: 0.325rem ${SPACING_1};
  border-radius: 4px;
`;

const NewsroomContentWrapper = styled.div`
  max-width: ${remCalc(576)};
  background: ${GREY_100};
  padding: 0 ${SPACING_4} 0 ${SPACING_4};
  position: relative;
  height: calc(100% - 3.75rem);
`;

const GroupOfLines = styled.div<{ $flexDirection?: 'row' | 'column'; $gapInRem?: number }>`
  display: flex;
  gap: ${props => props.$gapInRem || 0.4}rem;
  flex-direction: ${props => props.$flexDirection || 'row'};
  margin: 0.8rem 0;
  width: 100%;
`;

const Line = styled.div<{ $widthInPercentage?: number; $height?: number }>`
  width: ${props => (props.$widthInPercentage ? `${props.$widthInPercentage}%` : '50%')};
  background: ${GREY_500};
  height: ${props => props.$height || 4}px;
  border-radius: 6px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  button {
    color: white;
  }
  i {
    color: ${GREY_900};
    width: 0.75rem;
  }
`;

const FakeButton = styled.div<{ $color?: string }>`
  background: ${props => props.$color || PURPLE_600};
  color: white;
  user-select: none;
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  font-size: 0.75rem;
  line-height: 1rem;
  margin-left: ${SPACING_1};
  border: none;
`;

const PressreleaseContentWrapper = styled.div`
  width: 100%;
  height: auto;
  max-height: ${remCalc(200)};
  display: flex;
  flex-direction: row;
`;

const PressreleaseImagePlaceholder = styled.div`
  width: 55%;
  background-color: ${GREY_300};
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
`;

const PressreleaseTextContainer = styled.div`
  background-color: white;
  width: 45%;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  padding: ${remCalc(30)} ${remCalc(30)} ${remCalc(20)} ${remCalc(30)};
  position: relative;
`;

const PressreleaseTitle = styled.div<{ $color: string }>`
  border: 0.25rem solid ${props => props.$color};
  border-width: 0 0 0 0.25rem;
  opacity: 1;
  height: ${SIZING_2_5};
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 0 0rem ${SPACING_1};
  margin-bottom: 0.8rem;
`;

const PreviewBranding = () => {
  const {
    mndAssets: { cdnDistribution },
  } = useEnvironmentVariables();
  const { t } = useTranslation();
  const { newsroomData } = useNewsroomSetup();
  const imgUrl = getCloudinaryImageUrl(
    cdnDistribution,
    newsroomData.branding.logoCloudinaryId,
    null,
    'LOGOTYPE',
  );
  const { primaryBrandColor, secondaryBrandColor } = newsroomData.branding;

  return (
    <BrandingWrapper>
      <NewsroomHeader>
        {imgUrl ? (
          <Figure>
            <img src={imgUrl} alt="logotype" />
          </Figure>
        ) : (
          <LogoPlaceholder>{t('newsroom-setup_logo-here')}</LogoPlaceholder>
        )}
        <GroupOfLines $gapInRem={0.6}>
          <Line $widthInPercentage={8} />
          <Line $widthInPercentage={8} />
          <Line $widthInPercentage={8} />
          <Line $widthInPercentage={8} />
        </GroupOfLines>
        <ButtonWrapper>
          <Icons.Search />
          <FakeButton $color={secondaryBrandColor}>{t('newsroom-setup_follow')}</FakeButton>
        </ButtonWrapper>
      </NewsroomHeader>
      <NewsroomContentWrapper>
        <HeaderWithColoredUnderline as="h3" $lineColor={primaryBrandColor}>
          {t('newsroom-setup_latest-news')}
        </HeaderWithColoredUnderline>
        <PressreleaseContentWrapper>
          <PressreleaseImagePlaceholder />
          <PressreleaseTextContainer>
            <PressreleaseTitle $color={primaryBrandColor}>
              <Line $widthInPercentage={30} />
            </PressreleaseTitle>
            <GroupOfLines $flexDirection="column">
              <Line $height={12} $widthInPercentage={100} />
              <Line $height={12} $widthInPercentage={90} />
            </GroupOfLines>
            <GroupOfLines $flexDirection="column">
              <Line $widthInPercentage={100} />
              <Line $widthInPercentage={85} />
              <Line $widthInPercentage={90} />
              <Line $widthInPercentage={65} />
              <Line $widthInPercentage={95} />
              <Line $widthInPercentage={40} />
            </GroupOfLines>
          </PressreleaseTextContainer>
        </PressreleaseContentWrapper>
        <WrapperForEmptyReleases $position="bottom">
          <EmptyPressRelease $background={GREY_300} $border="top" />
          <EmptyPressRelease $background={GREY_300} $border="top" />
          <EmptyPressRelease $background={GREY_300} $border="top" />
        </WrapperForEmptyReleases>
      </NewsroomContentWrapper>
    </BrandingWrapper>
  );
};

export default PreviewBranding;
